import { useState, useEffect, useRef } from "react";
import { calculateImageSize, getImageDimensions } from "../../utils/imageUtils";

/**
 * Hook to calculate and maintain image dimensions for responsive display
 * Locks size to image's natural dimensions (scaled to fit viewport) to prevent
 * size changes when window dimensions change (e.g., network tab opening/closing)
 * @param {string} imageUrl - Image URL to measure
 * @returns {{imageSize: {width: number, height: number} | null}}
 */
export const useImageSize = (imageUrl) => {
  const [imageSize, setImageSize] = useState(null);
  const naturalDimensionsRef = useRef(null); // Store natural dimensions to prevent recalculation

  useEffect(() => {
    if (!imageUrl) {
      setImageSize(null);
      naturalDimensionsRef.current = null;
      return;
    }

    let cancelled = false;
    // Clear stored dimensions when image URL changes
    naturalDimensionsRef.current = null;

    const computeSize = async () => {
      try {
        // Get natural dimensions for this image
        const dimensions = await getImageDimensions(imageUrl);
        if (cancelled) return;
        
        // Store natural dimensions for this image
        naturalDimensionsRef.current = dimensions;

        const calculatedSize = calculateImageSize(
          dimensions.width,
          dimensions.height
        );
        
        if (calculatedSize && !cancelled) {
          setImageSize(calculatedSize);
        }
      } catch (error) {
        if (cancelled) return;
        // Fallback: clear size so CSS handles layout
        setImageSize(null);
        naturalDimensionsRef.current = null;
      }
    };

    // Only compute size when image URL changes, not on window resize
    // This ensures consistent sizing regardless of viewport changes (e.g., network tab)
    computeSize();

    return () => {
      cancelled = true;
    };
  }, [imageUrl]); // Only recalculate when imageUrl changes

  return { imageSize };
};


